// components/LabelValueList.jsx
import React from "react";
import PropTypes from "prop-types";
import { Box, Grid, Typography, Divider, Stack } from "@mui/material";

/**
 * Convert "camelCase" or "snake_case" to "Camel Case" for labels.
 */
const humanize = (key) =>
  key
    .replace(/_/g, " ")
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (s) => s.toUpperCase())
    .trim();

const LabelValueList = ({
  data,
  labelMap = {}, // optional { key: "Custom Label" }
  dateKeys = ["createdOn", "updatedOn"],
  hideEmpty = true, // skip null/undefined/"" rows
  emptyPlaceholder = "—",
  valueRender, // (key,value) => ReactNode  (optional)
  spacing = 2, // Grid spacing
  columns = { xs: 12, sm: 6 },
}) => {
  if (!data || typeof data !== "object") return null;

  return (
    <Stack divider={<Divider flexItem />} spacing={1}>
      {Object.entries(data).map(([key, value]) => {
        // hide row if requested and value is "empty"
        const isEmpty = value === null || value === undefined || value === "";
        if (hideEmpty && isEmpty) return null;

        // format dates
        let display =
          dateKeys.includes(key) && value
            ? new Date(value).toLocaleString("en-IN", {
                dateStyle: "medium",
              })
            : value;

        // custom renderer overrides all
        if (typeof valueRender === "function") {
          const custom = valueRender(key, value);
          if (custom !== undefined) display = custom;
        }

        if (!display && display !== 0) display = emptyPlaceholder;

        return (
          <Grid
            container
            key={key}
            spacing={spacing}
            columns={12}
            sx={{ py: 0.5 }}
          >
            <Grid item {...columns}>
              <Typography variant="subtitle2" color="text.secondary">
                {labelMap[key] ?? humanize(key)}
              </Typography>
            </Grid>
            <Grid item {...columns}>
              <Typography variant="body2">{display}</Typography>
            </Grid>
          </Grid>
        );
      })}
    </Stack>
  );
};

LabelValueList.propTypes = {
  data: PropTypes.object.isRequired,
  labelMap: PropTypes.object,
  dateKeys: PropTypes.arrayOf(PropTypes.string),
  hideEmpty: PropTypes.bool,
  emptyPlaceholder: PropTypes.node,
  valueRender: PropTypes.func,
  spacing: PropTypes.number,
  columns: PropTypes.object, // MUI Grid props
};

export default LabelValueList;
